/**
 * ChangePasswordView — the mandatory first-login password reset.
 *
 * Organizers are provisioned with a temporary password (mustChangePassword=true
 * in the session). The router renders this fullscreen view INSTEAD of the
 * dashboard until they choose a permanent password, so there's no way to skip
 * it. On success the server returns a fresh token; we swap the stored session,
 * clear the flag, and route into the dashboard.
 */

import { apiService } from "../services/api";

// Mirrors the server policy (organizer/update-initial-password): at least 8
// chars with a letter and a digit. Validated client-side for instant feedback;
// the server is the source of truth.
function validate(pw: string, confirm: string): string | null {
  if (pw.length < 8) return "Password must be at least 8 characters.";
  if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw)) {
    return "Password must include at least one letter and one number.";
  }
  if (pw !== confirm) return "Passwords do not match.";
  return null;
}

export class ChangePasswordView {
  private container: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="min-h-screen bg-gradient-to-br from-background-light to-slate-100 dark:from-background-dark dark:to-slate-900 daylight:from-day-base daylight:to-day-sunken flex items-center justify-center p-4">
        <div class="w-full max-w-md bg-white dark:bg-card-dark daylight:bg-day-card rounded-2xl shadow-2xl daylight:shadow-day-md border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 p-8 md:p-10">
          <div class="text-center mb-8">
            <div class="flex items-center justify-center size-14 bg-gradient-to-br from-primary to-accent rounded-xl mx-auto mb-4 shadow-lg">
              <span class="material-symbols-outlined text-white text-4xl">lock_reset</span>
            </div>
            <h1 class="text-2xl md:text-3xl font-black text-slate-900 dark:text-white daylight:text-day-ink mb-2">Choose a permanent password</h1>
            <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-sm">For your security, set a new password before continuing to your dashboard.</p>
          </div>

          <form id="change-password-form" class="space-y-5">
            <div>
              <label class="block text-sm font-semibold text-slate-900 dark:text-white daylight:text-day-ink mb-2">New password</label>
              <input
                type="password"
                id="new-password"
                required
                autocomplete="new-password"
                class="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 bg-white dark:bg-slate-800 daylight:bg-day-raised rounded-lg text-slate-900 dark:text-white daylight:text-day-ink placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition"
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-slate-900 dark:text-white daylight:text-day-ink mb-2">Confirm new password</label>
              <input
                type="password"
                id="confirm-password"
                required
                autocomplete="new-password"
                class="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 bg-white dark:bg-slate-800 daylight:bg-day-raised rounded-lg text-slate-900 dark:text-white daylight:text-day-ink placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition"
                placeholder="Re-enter your new password"
              />
            </div>

            <p class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Use at least 8 characters, including a letter and a number.</p>

            <div id="change-password-error" class="hidden bg-error/10 border border-error/30 text-error rounded-lg p-4 text-sm font-medium flex items-start gap-3">
              <span class="material-symbols-outlined flex-shrink-0">error</span>
              <span id="change-password-error-text"></span>
            </div>

            <button
              type="submit"
              id="change-password-submit"
              class="w-full bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 text-white font-bold py-3 px-4 rounded-lg shadow-lg shadow-primary/30 transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span class="material-symbols-outlined">check_circle</span>
              <span>Set password &amp; continue</span>
            </button>
          </form>
        </div>
      </div>
    `;

    this.setupListeners();
  }

  private setupListeners(): void {
    const form = this.container.querySelector("#change-password-form") as HTMLFormElement;
    const errorDiv = this.container.querySelector("#change-password-error") as HTMLElement;
    const errorText = this.container.querySelector("#change-password-error-text") as HTMLElement;
    const submitBtn = this.container.querySelector("#change-password-submit") as HTMLButtonElement;

    const showError = (message: string) => {
      errorText.textContent = message;
      errorDiv.classList.remove("hidden");
    };

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      errorDiv.classList.add("hidden");

      const newPassword = (this.container.querySelector("#new-password") as HTMLInputElement).value;
      const confirmPassword = (this.container.querySelector("#confirm-password") as HTMLInputElement).value;

      const validationError = validate(newPassword, confirmPassword);
      if (validationError) {
        showError(validationError);
        return;
      }

      submitBtn.disabled = true;
      try {
        const response = await apiService.updateInitialPassword(newPassword, confirmPassword);

        // Swap in the fresh session and clear the forced-reset gate.
        if (response.token) localStorage.setItem("authToken", response.token);
        localStorage.removeItem("mustChangePassword");
        if (response.user?.role) localStorage.setItem("userRole", response.user.role);

        // Route into the active dashboard. The hashchange handler re-runs the
        // router, which now passes the (cleared) forced-reset gate.
        window.location.hash = "#home";
        // If already on #home, hashchange won't fire — force a re-render.
        const { router } = await import("../main");
        await router();
      } catch (error) {
        console.error("[ChangePassword] Failed:", error);
        showError(error instanceof Error ? error.message : "Failed to update password. Please try again.");
        submitBtn.disabled = false;
      }
    });
  }
}
